// /src/pages/admin/UserManagementPage.tsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Terminal, Key } from 'lucide-react';
import PortalLayout from '@/components/portal/PortalLayout';
// AdminRoute removed - AuthMiddleware handles all permission checking
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from '@/contexts/AuthContext';
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { StripeUserManagement } from "@/components/admin/StripeUserManagement";
import { UserTable } from "@/components/admin/users/UserTable";
import { CreditCard } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

// Define the expected shape of the data returned by the RPC function
// Based on the function definition in the migration
interface AdminUser {
  id: string;
  email: string | null;
  name: string | null;
  subscription_status: string | null;
  role: string | null;
  is_banned: boolean | null;
  created_at: string | null;
  last_sign_in_at: string | null;
}

// Define the type for the RPC response
type AdminUserListResponse = AdminUser[] | null;

// Type for the update_user_role RPC function parameters
interface UpdateUserRoleParams {
  target_user_id_input: string;
  new_role_input: string;
}

// Type for the expected JSON response from update_user_role
interface UpdateUserRoleResponse {
  status: number;
  message: string;
  user_id?: string;
  new_role?: string;
  details?: string; // For error details
}

// Type for admin_update_user_details RPC function parameters
interface UpdateUserDetailsParams {
  target_user_id_input: string;
  new_subscription_status_input?: string | null; // Optional
  new_is_banned_input?: boolean | null;      // Optional
}

// Type for expected JSON response from admin_update_user_details
interface UpdateUserDetailsResponse {
  status: number;
  message: string;
  user_id?: string;
  new_subscription_status?: string;
  new_is_banned?: boolean;
  details?: string;
}

const fetchAdminUserList = async (): Promise<AdminUserListResponse> => {
  const { getAdminUserList } = await import('@/api/admin');
  const data = await getAdminUserList();

  // Return user data with last_sign_in_at as null for now
  // This avoids the auth.users access issue
  const usersWithSignInData = data.map((user) => ({
    ...user,
    last_sign_in_at: null, // Will show 'N/A' in the UI
  }));

  return usersWithSignInData;
};

// Sub-component for each user row to manage its own state for role editing
interface UserRowProps {
  user: AdminUser;
}

const UserRow: React.FC<UserRowProps> = ({ user }) => {
  const [selectedRole, setSelectedRole] = useState<string>(user.role || 'user');
  const [editableSubscriptionStatus, setEditableSubscriptionStatus] = useState<string>(user.subscription_status || '');
  const [editableIsBanned, setEditableIsBanned] = useState<boolean>(user.is_banned || false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user: currentAuthUser } = useAuth();

  const updateUserRoleMutation = useMutation<
    UpdateUserRoleResponse, // Expected success response type
    Error, // Error type
    UpdateUserRoleParams // Variables type for the mutation
  >(
    {
      mutationFn: async (params) => {
        const { updateUserRole } = await import('@/api/admin');
        return await updateUserRole(params.target_user_id_input, params.new_role_input);
      },
      onSuccess: (data) => {
        if (data && data.status === 200) {
          toast({
            title: "Success!",
            description: data.message || "User role updated successfully.",
          });
          queryClient.invalidateQueries({ queryKey: ['adminUserList'] });
        } else {
          // Handle cases where the RPC itself succeeded but the operation within it might have a non-200 status
          toast({
            title: "Update Issue",
            description: data?.message || "Could not update role.",
            variant: "destructive",
          });
        }
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message || "An unexpected error occurred.",
          variant: "destructive",
        });
      },
    }
  );

  const updateUserDetailsMutation = useMutation<
    UpdateUserDetailsResponse,
    Error,
    UpdateUserDetailsParams
  >(
    {
      mutationFn: async (params) => {
        const { updateUserDetails } = await import('@/api/admin');
        const updates: any = {};
        if (params.new_subscription_status_input !== undefined) {
          updates.subscription_status = params.new_subscription_status_input;
        }
        if (params.new_is_banned_input !== undefined) {
          updates.is_banned = params.new_is_banned_input;
        }
        return await updateUserDetails(params.target_user_id_input, updates);
      },
      onSuccess: (data) => {
        if (data && data.status === 200) {
          toast({
            title: "Success!",
            description: data.message || "User details updated successfully.",
          });
          queryClient.invalidateQueries({ queryKey: ['adminUserList'] });
          // Potentially reset local state to match new data if needed, or rely on query refetch
          // For now, query refetch will handle it.
        } else {
          toast({
            title: "Update Issue",
            description: data?.message || "Could not update user details.",
            variant: "destructive",
          });
        }
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message || "An unexpected error occurred while updating details.",
          variant: "destructive",
        });
      },
    }
  );

  // Password reset mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async ({ userId, newPassword }: { userId: string; newPassword: string }) => {
      const { resetUserPassword } = await import('@/api/admin');
      return await resetUserPassword(userId, newPassword);
    },
    onSuccess: () => {
      toast({
        title: "Success!",
        description: "Password reset successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to reset password.",
        variant: "destructive",
      });
    },
  });

  const handleUpdateRole = () => {
    if (selectedRole === user.role) {
      toast({
        title: "No Change",
        description: "The selected role is the same as the current role.",
      });
      return;
    }
    updateUserRoleMutation.mutate({
      target_user_id_input: user.id,
      new_role_input: selectedRole,
    });
  };

  const handleSaveDetails = () => {
    // Check if actual changes were made
    const statusChanged = editableSubscriptionStatus !== (user.subscription_status || '');
    const banChanged = editableIsBanned !== (user.is_banned || false);

    if (!statusChanged && !banChanged) {
      toast({
        title: "No Changes",
        description: "Subscription status and ban status are the same as current values.",
      });
      return;
    }

    const params: UpdateUserDetailsParams = { target_user_id_input: user.id };
    if (statusChanged) {
      params.new_subscription_status_input = editableSubscriptionStatus;
    }
    if (banChanged) {
      params.new_is_banned_input = editableIsBanned;
    }
    
    updateUserDetailsMutation.mutate(params);
  };

  const currentAuthUserId = currentAuthUser?.id;
  const isCurrentUser = user.id === currentAuthUserId;

  const isRoleUpdatePending = updateUserRoleMutation.isPending;
  const isDetailsUpdatePending = updateUserDetailsMutation.isPending;
  const isAnyMutationPending = isRoleUpdatePending || isDetailsUpdatePending;

  // Disable role change if it's the current admin trying to demote themselves
  const disableRoleChangeForSelf = isCurrentUser && selectedRole !== 'admin' && user.role === 'admin';
  // Disable banning self
  const disableBanForSelf = isCurrentUser && editableIsBanned;

  const handlePasswordReset = () => {
    if (!newPassword || newPassword.length < 6) {
      toast({
        title: "Invalid Password",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      return;
    }

    resetPasswordMutation.mutate({ userId: user.id, newPassword });
    setIsPasswordDialogOpen(false);
    setNewPassword('');
  };

  return (
    <TableRow key={user.id}>
      <TableCell className="font-medium">{user.email || 'N/A'}</TableCell>
      <TableCell>{user.name || 'N/A'}</TableCell>
      <TableCell>
        <Select
          value={selectedRole}
          onValueChange={setSelectedRole}
          disabled={isAnyMutationPending || disableRoleChangeForSelf}
        >
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Select role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <Input 
          type="text"
          value={editableSubscriptionStatus}
          onChange={(e) => setEditableSubscriptionStatus(e.target.value)}
          className="w-[150px]"
          disabled={isAnyMutationPending}
        />
      </TableCell>
      <TableCell className="text-center">
        <div className="flex items-center space-x-2">
          <Switch 
            id={`ban-switch-${user.id}`}
            checked={editableIsBanned}
            onCheckedChange={setEditableIsBanned}
            disabled={isAnyMutationPending || disableBanForSelf}
          />
          <Label htmlFor={`ban-switch-${user.id}`}>{editableIsBanned ? "Banned" : "Active"}</Label>
        </div>
      </TableCell>
      <TableCell>{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</TableCell>
      <TableCell>{user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString() : 'N/A'}</TableCell>
      <TableCell>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <CreditCard className="h-4 w-4 mr-2" />
              {user.stripe_customer_id ? 'Manage' : 'Setup'}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Stripe Management - {user.email}</DialogTitle>
            </DialogHeader>
            <StripeUserManagement
              user={user}
              onUserUpdate={() => {
                // Refresh the user list
                queryClient.invalidateQueries({ queryKey: ['adminUserList'] });
              }}
            />
          </DialogContent>
        </Dialog>
      </TableCell>
      <TableCell className="space-x-2 flex items-center">
        <Button
          onClick={handleUpdateRole}
          disabled={isAnyMutationPending || selectedRole === user.role || disableRoleChangeForSelf}
          size="sm"
          variant="outline"
        >
          {isRoleUpdatePending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          Update Role
        </Button>
        <Button
          onClick={handleSaveDetails}
          disabled={isAnyMutationPending || 
            (editableSubscriptionStatus === (user.subscription_status || '') && 
             editableIsBanned === (user.is_banned || false)) || 
             disableBanForSelf
           }
          size="sm"
        >
          {isDetailsUpdatePending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : null}
          Save Details
        </Button>
        <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={isAnyMutationPending}
            >
              <Key className="mr-2 h-4 w-4" />
              Reset Password
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                Enter a new password for {user.email}. The user will be able to sign in with this new password immediately.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  minLength={6}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsPasswordDialogOpen(false);
                setNewPassword('');
              }}>
                Cancel
              </Button>
              <Button
                onClick={handlePasswordReset}
                disabled={resetPasswordMutation.isPending || !newPassword || newPassword.length < 6}
              >
                {resetPasswordMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Reset Password
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </TableCell>
    </TableRow>
  );
};

const UserManagementPage: React.FC = () => {
  return (
    <PortalLayout>
      <div className="container mx-auto py-6 space-y-6">
        <UserTable />
      </div>
    </PortalLayout>
  );
};

export default UserManagementPage;
